import { createRoute, Outlet, redirect } from "@tanstack/react-router";
import {
  ContentLibrary,
  ToReviewContentLibrary,
  UnassignedContentLibrary,
} from "pages/app/ContentLibrary";
import { Flex, Text } from "styles/utility-components";

import { DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import ContentLibraryNavigation from "components/content_library/ContentLibraryNavigation";
import { ProviderType } from "components/integrations/managed_integrations/types";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import { useSubscription } from "lib/hooks/subscription";
import { uploadContextLayoutRoute } from "./uploadContext";

export function ContentLibraryLayout() {
  const subscription = useSubscription();
  const bidsPaywall = !subscription.hasDataTypes("BIDS");

  if (bidsPaywall) {
    return (
      <DetailsPage>
        <DetailsHeader>
          <Flex gap={8} alignItems="center">
            <Text h1>Content Library</Text>
          </Flex>
        </DetailsHeader>
        <Flex data-testid="content-library-paywall-banner">
          <PaywallBanner featureType="CREATE_BIDS" />
        </Flex>
      </DetailsPage>
    );
  }

  return (
    <ContentLibraryNavigation>
      <Outlet />
    </ContentLibraryNavigation>
  );
}

export const contentLibraryLayoutRoute = createRoute({
  getParentRoute: () => uploadContextLayoutRoute,
  path: "/content-library",
  staticData: { pageName: "Content library", breadcrumbLabel: "Content library" },
  component: ContentLibraryLayout,
});

export const contentLibraryIndexRoute = createRoute({
  getParentRoute: () => contentLibraryLayoutRoute,
  path: "/",
  staticData: { pageName: "" },
  beforeLoad: () => {
    throw redirect({
      to: "/content-library/$provider",
      params: { provider: "uploads" },
      replace: true,
    });
  },
});

export const toReviewRoute = createRoute({
  getParentRoute: () => contentLibraryLayoutRoute,
  path: "/to-review",
  staticData: { pageName: "Content library - To review", breadcrumbLabel: "To review" },
  component: ToReviewContentLibrary,
});

export const unassignedRoute = createRoute({
  getParentRoute: () => contentLibraryLayoutRoute,
  path: "/unassigned",
  staticData: { pageName: "Content library - Unassigned", breadcrumbLabel: "Unassigned" },
  component: UnassignedContentLibrary,
});

export const providerLayoutRoute = createRoute({
  getParentRoute: () => contentLibraryLayoutRoute,
  path: "/$provider",
  staticData: { pageName: "" },
  component: Outlet,
});

export const providerIndexRoute = createRoute({
  getParentRoute: () => providerLayoutRoute,
  path: "/",
  staticData: { pageName: "Content library" },
  component: function ProviderIndexRouteComp() {
    const { provider } = providerIndexRoute.useParams();
    return <ContentLibrary provider={provider as ProviderType | "uploads"} />;
  },
});

export const providerFolderRoute = createRoute({
  getParentRoute: () => providerLayoutRoute,
  path: "/$",
  staticData: { pageName: "Content library" },
  component: function ProviderFolderRouteComp() {
    const { provider, _splat } = providerFolderRoute.useParams();
    return <ContentLibrary provider={provider as ProviderType | "uploads"} folderPath={_splat} />;
  },
});

export const contentLibraryRouteTree = contentLibraryLayoutRoute.addChildren([
  contentLibraryIndexRoute,
  toReviewRoute,
  unassignedRoute,
  providerLayoutRoute.addChildren([providerIndexRoute, providerFolderRoute]),
]);
